import { createAsyncThunk } from '@reduxjs/toolkit';
import { getAttempts, getGatewayType, getOrderingProvider } from '../selectors/config';
import getValidateBody from '../selectors/getValidateBody';
import { SALE_TYPE } from '../constants/saleType';
import { getLocation } from '../selectors';
import smoothPickupTime from '../utils/ordering/smoothPickupTime';
import lodash from 'lodash';
import Logger from '../utils/Logger';
import Api, { AuthenticationMethod, FetchParams } from '../utils/Api';
import { FAILURE_REASON, PAYMENT_GATEWAY_TYPES } from '../constants';
import getReceiptType from '../selectors/getReceiptType';
import { constructSalePaymentRequest } from '../thunks/constructSalePaymentRequest';
import { ORDERING_PROVIDER } from '../constants/orderingProvider';

export const validateOrder = createAsyncThunk(
  '$validateOrder',
  async (
    data: {
      authenticationMethod?: AuthenticationMethod;
    },
    { dispatch, getState, rejectWithValue },
  ) => {
    const { authenticationMethod = 'trusted' } = data;
    let response;
    let valid = false;
    let reason;
    let errorText: string = '';
    try {
      const gatewayType = getGatewayType(getState() as EntireFrontendState);
      const attemptsConfig = getAttempts(getState() as EntireFrontendState);
      let attemptsRemaining = attemptsConfig.validateOrder;
      const receiptType = getReceiptType(getState() as EntireFrontendState);
      const orderingProvider = getOrderingProvider(
        getState() as EntireFrontendState,
      );
      // Init body
      let body: any = {};

      // While working on TYRO I just realised STRIPE doesn't use constructSalePaymentRequest to get the body instead it uses getValidateBody
      // and modifies the body here but for TYRO we don't need to do it. I believe in future STRIPE should also use constructSalePaymentRequest
      // to get the body as this is the actual body that we pass for sale call

      if (gatewayType === PAYMENT_GATEWAY_TYPES.TYRO) {
        const { body: saleBody } = await dispatch(
          constructSalePaymentRequest({
            route: 'checkout',
            authenticationMethod,
          }),
        ).unwrap();

        body = saleBody;
      } else {
        const baseBody = getValidateBody(getState() as EntireFrontendState);

        body = {
          ...baseBody,
        };

        if (body.SaleType === SALE_TYPE.TABLE_ORDER) {
          body.PickupTime = 'ASAP';
        }

        if (body.PickupTime != undefined) {
          const location = getLocation(getState() as EntireFrontendState);
          body.PickupTime = smoothPickupTime(
            lodash.get(baseBody, 'PickupTime'),
            location?.averageOrderWaitTime || 0,
          );
        }

        if (receiptType === 'none' && body.Details.Email) {
          body.Details.Email = undefined;
        }
      }

      // Logger.log('validating order:', undefined, JSON.stringify(body));
      Logger.log(JSON.stringify(body, null, 2));

      const validateApiPathMapper = {
        [PAYMENT_GATEWAY_TYPES.STRIPE]: {
          trusted: '/api/v1/sale/validate_cart',
          member: '/api/v1/sale/validate_cart',
          none: '/api/v1/sale/validate_cart/nonmember',
        }[authenticationMethod],
        [PAYMENT_GATEWAY_TYPES.TYRO]: '/api/v1/sale/token_validate_cart',
      };

      const params: FetchParams = {
        // for kiosk lets default to stripe
        path: validateApiPathMapper[orderingProvider === ORDERING_PROVIDER.KIOSK ? PAYMENT_GATEWAY_TYPES.STRIPE : gatewayType],
        method: 'POST',
        body,
        applicationErrorAllowed: true,
      };

      while (attemptsRemaining) {
        attemptsRemaining -= 1;

        response = await Api.fetch(params, authenticationMethod);

        Logger.log(`validation response: ${JSON.stringify(response)}`);
        valid = response.success;
        if (valid) {
          break;
        }
      }

      // process validation error
      if (!valid) {
        reason = FAILURE_REASON.UNKNOWN;
        errorText = lodash.get(response, 'error', '');

        let extraInfo = lodash.get(response, 'extra_info');
        //@ts-ignore
        extraInfo = Array.isArray(extraInfo) ? extraInfo : [];

        let firstValidationMsg;

        try {
          //@ts-ignore
          firstValidationMsg = extraInfo
            //@ts-ignore
            .map(
              (err?: { msg?: string }) =>
                lodash.get(err, 'msg', '').trim() || undefined,
            )
            .filter((msg?: string) => Boolean(msg))[0];
          // eslint-disable-next-line
        } catch (e) {
          Logger.log('problem parsing extra info');
        }

        if (firstValidationMsg) {
          errorText += `: ${firstValidationMsg}`;
        }

        if (errorText.includes('uncontactable')) {
          reason = FAILURE_REASON.LOCATION_OFFLINE;
        } else if (
          errorText.includes('hours') ||
          errorText.includes('closed')
        ) {
          reason = FAILURE_REASON.LOCATION_CLOSED;
        }
      }

      return {
        valid,
        reason,
        errorText,
      };
    } catch (e) {
      console.error('Validate order failed', e);
      return rejectWithValue({
        e,
        reason:
          lodash.get(e, 'details.type', '' as string) === 'connectivity'
            ? FAILURE_REASON.FETCH_FAILED
            : undefined,
        errorText,
      });
    }
  },
);
